import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";

const areas = [
  {
    city: "Los Angeles",
    state: "CA",
    zip: "90001",
    region: "Greater LA",
    desc: "From the San Fernando Valley to South LA, we connect homeowners across Los Angeles with licensed tree service professionals. LA's diverse urban canopy — palms, eucalyptus, jacarandas — requires specialists who know local species and city permit requirements.",
    avgTrim: "$350–$1,200",
    avgRemoval: "$600–$2,500",
    avgStump: "$150–$400",
    trees: ["Palm Trees", "Eucalyptus", "Jacaranda", "Ficus", "Pepper Trees"],
    image: "https://public.readdy.ai/ai/img_res/1573d6460671aa389ff39dd1009ea2e0.jpg",
    geo: { lat: "34.0522", lon: "-118.2437", region: "US-CA" },
  },
  {
    city: "Simi Valley",
    state: "CA",
    zip: "93063",
    region: "Ventura County",
    desc: "Simi Valley's hillside properties and mature oak woodlands require experienced arborists familiar with fire-risk zones and slope access. We match Simi Valley homeowners with pros who understand local fire clearance requirements and HOA guidelines.",
    avgTrim: "$275–$900",
    avgRemoval: "$500–$2,000",
    avgStump: "$100–$300",
    trees: ["Valley Oak", "Coast Live Oak", "Sycamore", "Pepper Trees", "Eucalyptus"],
    image: "https://readdy.ai/api/search-image?query=Simi-Valley-California-hillside-residential-neighborhood-oak-trees-suburban-homes-clear-blue-sky-dry-hills-background&width=600&height=380&seq=area-simi-001&orientation=landscape",
    geo: { lat: "34.2694", lon: "-118.7815", region: "US-CA" },
  },
  {
    city: "Woodland Hills",
    state: "CA",
    zip: "91364",
    region: "West San Fernando Valley",
    desc: "Woodland Hills is known for its mature tree canopy and upscale properties. Tree trimming in this area often involves large specimen trees, proximity to structures, and fire-safe landscaping requirements. Our vetted pros handle it all.",
    avgTrim: "$300–$1,100",
    avgRemoval: "$550–$2,200",
    avgStump: "$125–$350",
    trees: ["Coast Live Oak", "Sycamore", "Liquidambar", "Magnolia", "Jacaranda"],
    image: "https://readdy.ai/api/search-image?query=Woodland-Hills-California-upscale-residential-neighborhood-large-mature-trees-green-lawns-suburban-homes-San-Fernando-Valley&width=600&height=380&seq=area-wh-001&orientation=landscape",
    geo: { lat: "34.1684", lon: "-118.6059", region: "US-CA" },
  },
  {
    city: "Fresno",
    state: "CA",
    zip: "93701",
    region: "Central Valley",
    desc: "Fresno's Central Valley climate supports a wide variety of trees, from ornamental fruit trees to large shade trees. The city's urban forestry program means many trees require permits before removal. Our local pros know the rules and get the job done right.",
    avgTrim: "$200–$750",
    avgRemoval: "$400–$1,800",
    avgStump: "$100–$275",
    trees: ["Valley Oak", "Crape Myrtle", "Ash", "Mulberry", "Chinese Pistache"],
    image: "https://readdy.ai/api/search-image?query=Fresno-California-residential-neighborhood-tree-lined-street-mature-shade-trees-suburban-homes-Central-Valley-warm-sunny-day&width=600&height=380&seq=area-fresno-001&orientation=landscape",
    geo: { lat: "36.7378", lon: "-119.7871", region: "US-CA" },
  },
];

export default function ServiceAreasPage() {
  const navigate = useNavigate();
  const [activeArea, setActiveArea] = useState(0);

  return (
    <div className="min-h-screen bg-white font-sans">
      <SiteNavbar />

      {/* Hero */}
      <section className="bg-[#1B4332] pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block bg-green-400/20 border border-green-400/30 text-green-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            Southern &amp; Central California
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 leading-tight">
            Tree Service Areas We Serve
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-8">
            We connect homeowners in Los Angeles, Simi Valley, Woodland Hills, Fresno, and surrounding communities with licensed, insured tree service professionals.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {areas.map((a, i) => (
              <button
                key={a.city}
                onClick={() => setActiveArea(i)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeArea === i
                    ? "bg-white text-[#1B4332]"
                    : "bg-white/15 text-white hover:bg-white/25"
                }`}
              >
                {a.city}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Area Detail */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {areas.map((area, i) => (
            <div
              key={area.city}
              className={`${activeArea === i ? "block" : "hidden"}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start mb-14">
                {/* Image */}
                <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden">
                  <img
                    src={area.image}
                    alt={`Tree service in ${area.city}, ${area.state}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Info */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5E9] rounded-xl">
                      <i className="ri-map-pin-2-line text-[#1B4332] text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{area.region}</p>
                      <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B4332]">
                        {area.city}, {area.state}
                      </h2>
                    </div>
                  </div>
                  <p className="text-gray-600 text-base leading-relaxed mb-7">{area.desc}</p>

                  {/* Pricing */}
                  <div className="bg-[#F1F8F4] rounded-xl p-5 mb-6">
                    <p className="text-[#1B4332] font-bold text-sm mb-3">Typical Prices in {area.city}</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: "Trimming", price: area.avgTrim },
                        { label: "Removal", price: area.avgRemoval },
                        { label: "Stump", price: area.avgStump },
                      ].map((p) => (
                        <div key={p.label} className="text-center">
                          <p className="text-[#1B4332] font-extrabold text-sm">{p.price}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{p.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Common Trees */}
                  <div className="mb-7">
                    <p className="text-[#1B4332] font-semibold text-sm mb-2">Common Trees in This Area</p>
                    <div className="flex flex-wrap gap-2">
                      {area.trees.map((t) => (
                        <span key={t} className="bg-[#E8F5E9] text-[#1B4332] text-xs font-medium px-3 py-1 rounded-full">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => navigate("/free-quote")}
                    className="inline-flex items-center gap-2 bg-[#1B4332] text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#2d6a4f] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get a Free Quote in {area.city}
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* All Areas Grid */}
          <div className="border-t border-gray-100 pt-14">
            <h2 className="text-2xl font-extrabold text-[#1B4332] mb-8 text-center">All Service Areas at a Glance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {areas.map((area, i) => (
                <div key={area.city} className="bg-[#F8FAF9] rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1B4332] rounded-xl mb-3">
                    <i className="ri-map-pin-line text-white text-base"></i>
                  </div>
                  <h3 className="font-bold text-[#1B4332] text-base">{area.city}</h3>
                  <p className="text-gray-400 text-xs mt-0.5 mb-3">{area.region} · {area.state}</p>
                  <p className="text-[#1B4332] font-semibold text-xs mb-4">Trim: {area.avgTrim}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setActiveArea(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className="flex-1 text-xs font-semibold text-[#1B4332] border border-[#1B4332]/30 hover:border-[#1B4332] py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      View Details
                    </button>
                    <a
                      href={`/tree-service/${area.city.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/tree-service/${area.city.toLowerCase().replace(/\s+/g, "-")}`); }}
                      className="flex-1 text-xs font-semibold text-white bg-[#1B4332] hover:bg-[#2d6a4f] py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-center"
                    >
                      City Page
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1B4332]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Don&apos;t See Your City?
          </h2>
          <p className="text-white/70 text-base mb-8">
            We serve hundreds of zip codes across California. Submit your info and we&apos;ll find a licensed pro near you.
          </p>
          <button
            onClick={() => navigate("/free-quote")}
            className="inline-flex items-center gap-3 bg-white text-[#1B4332] font-bold text-base px-8 py-4 rounded-full hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Check My Area <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
